import React, { useEffect } from 'react'
import { Route, Redirect } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'isEmpty';
import RoutesEnum from 'constants/routesEnum';
import useAuth from 'src/hooks/useAuth';
import { RootStateTypes } from 'ts/reducer';
import { uploadDocumentStatus } from 'constants/contants';

const PrivateRoute = ({ component: Component, name, isNotPrivate, children, auth, title, ...rest }: any) => (
    <Route
        {...rest}
        render={props => {
            return <ShowRoutes
                {...props}
                auth={auth}
                name={name}
                isNotPrivate={isNotPrivate}
                children={children}
                Component={Component}
                title={title}
            />
        }
        }
    />
);

const ShowRoutes = ({ Component, name, children, title, auth, isNotPrivate, ...rest }: any) => {
    const { isAuthenticated } = auth;
    const authed = useAuth()
    const { user } = useSelector((state: RootStateTypes) => state.auth)

    useEffect(() => {
        const { location: { pathname } } = rest;
        if (!isEmpty(title)) {
            document.title = title;
            rest.location.title = title
        } else {
            let docTitle = pathname.split('/');
            if (!isEmpty(docTitle) && docTitle.length > 0 && Array.isArray(docTitle)) {
                docTitle = docTitle[docTitle.length - 1].toUpperCase();
            } else {
                docTitle = "TIMS"
            }
            document.title = docTitle;
        }
    }, []);

    // ✅ Check authentication FIRST
    if (!isAuthenticated && !isNotPrivate) {
        return <Redirect to={RoutesEnum.Login} />
    }

    // ✅ Public routes bypass all checks
    if (isNotPrivate) {
        return <Component {...rest} title={title} children={children} />
    }

    // ✅ User is authenticated from here
    if (isAuthenticated && authed) {
        const pathname = rest.location.pathname;

        // ✅ ENFORCEMENT: UserType 2 (TrekkingAgency) document verification flow
        if (authed.UserType === 2 && user) {

            // ✅ Check if user is FULLY APPROVED
            const isFullyApproved = (user.IsDocumentUploaded && (user.IsVerified === true || user.IsApproved === true));

            // ✅ UserType 2 CANNOT access Dashboard even after approval
            if (pathname === RoutesEnum.Dashboard || pathname === '/') {
                if (!user.IsDocumentUploaded) {
                    // Not uploaded → Go to upload documents
                    return <Redirect to={RoutesEnum.UploadDocuments} />
                }
                if (user.IsDocumentUploaded && user.IsVerified === null) {
                    // Uploaded, waiting verification → Status sent page
                    return <Redirect to={RoutesEnum.UploadDocumentsStatus + '/' + uploadDocumentStatus.sent} />
                }
                if (user.IsDocumentUploaded && user.IsVerified === false) {
                    // Rejected → Status rejected page
                    return <Redirect to={RoutesEnum.UploadDocumentsStatus + '/' + uploadDocumentStatus.rejected} />
                }
                if (user.IsDocumentUploaded && user.IsVerified === true && !user.IsApproved) {
                    // Verified, waiting admin → Status approved page
                    return <Redirect to={RoutesEnum.UploadDocumentsStatus + '/' + uploadDocumentStatus.approved} />
                }
                if (isFullyApproved) {
                    // ✅ Fully approved → Go to NewRegister (NOT Dashboard)
                    return <Redirect to={RoutesEnum.NewRegister} />
                }
            }

            // ✅ If NOT fully approved, restrict access to other pages
            if (!isFullyApproved) {

                // 📋 WHITELIST: Routes allowed BEFORE approval
                const allowedPreApprovalRoutes = [
                    RoutesEnum.UploadDocuments,           // Can upload/re-upload documents
                    RoutesEnum.UploadDocumentsStatus,    // Can view status pages
                    RoutesEnum.Logout,                    // Can logout
                    RoutesEnum.accountSettings,           // Can change account settings
                    RoutesEnum.UserPassword,              // Can change password
                ];

                const isAllowedRoute = allowedPreApprovalRoutes.some(route => pathname.includes(route));

                // Determine redirect path based on document status
                let redirectPath = null;

                if (!user.IsDocumentUploaded) {
                    redirectPath = RoutesEnum.UploadDocuments;
                }
                else if (user.IsDocumentUploaded && user.IsVerified === null) {
                    redirectPath = RoutesEnum.UploadDocumentsStatus + '/' + uploadDocumentStatus.sent;
                }
                else if (user.IsDocumentUploaded && user.IsVerified === false) {
                    redirectPath = RoutesEnum.UploadDocumentsStatus + '/' + uploadDocumentStatus.rejected;
                }
                else if (user.IsDocumentUploaded && user.IsVerified === true && !user.IsApproved) {
                    redirectPath = RoutesEnum.UploadDocumentsStatus + '/' + uploadDocumentStatus.approved;
                }

                // If trying to access restricted route (not in whitelist), redirect
                if (redirectPath && !isAllowedRoute) {
                    console.warn(`UserType 2 blocked from ${pathname}, redirecting to ${redirectPath}`);
                    return <Redirect to={redirectPath} />
                }
            }

            // ✅ If fully approved, allow access to other pages (except Dashboard)
            if (isFullyApproved && pathname !== RoutesEnum.Dashboard && pathname !== '/') {
                // Validate status page matches actual user status (if trying to access status page)
                if (pathname.includes(RoutesEnum.UploadDocumentsStatus)) {
                    const statusType = rest.match.params.type;
                    let isValidStatusPage = false;

                    if (statusType === uploadDocumentStatus.sent) {
                        isValidStatusPage = (user.IsDocumentUploaded && user.IsVerified === null);
                    }
                    else if (statusType === uploadDocumentStatus.rejected) {
                        isValidStatusPage = (user.IsDocumentUploaded && user.IsVerified === false);
                    }
                    else if (statusType === uploadDocumentStatus.approved) {
                        isValidStatusPage = (user.IsVerified === true && user.IsApproved === true);
                    }

                    if (!isValidStatusPage) {
                        console.warn(`Invalid status page, user status mismatch`);
                        return <Redirect to={RoutesEnum.NewRegister} />
                    }
                }

                return <Component {...rest} title={title} children={children} />
            }
        }

        // ✅ Original logic for other user types (1, 4, 5, 6)
        if (authed && !authed[name] && authed.UserType !== 2) {
            if ((pathname === RoutesEnum.Dashboard || pathname === '/')) {
                if (authed.UserType == 1) {
                    return <Redirect to={RoutesEnum.NewRegister} />
                }
                if (authed.UserType == 5) {
                    return <Redirect to={RoutesEnum.NewRegister} />
                }
                if (authed.UserType == 4) {
                    return <Redirect to={RoutesEnum.Dashboard} />
                }
                if (authed.UserType == 6) {
                    return <Redirect to={RoutesEnum.Dashboard} />
                }
            }
        }

        return <Component {...rest} title={title} children={children} />
    }

    return <Redirect to={RoutesEnum.Login} />
}

PrivateRoute.propTypes = {
    auth: PropTypes.object.isRequired
}

const mapStateToProps = (state: any) => ({
    auth: state.auth
})

export default connect(mapStateToProps)(PrivateRoute);